import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: any | null;
   isAdmin: boolean;
   isModerator: boolean;
   isSuperAdmin: boolean;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any>(null);
   const [isAdmin, setIsAdmin] = useState(false);
   const [isModerator, setIsModerator] = useState(false);
   const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .single();
    setProfile(data);
  };

   const checkRoles = async (userId: string, email?: string) => {
     const [adminRes, modRes, superRes] = await Promise.all([
       supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
       supabase.rpc("has_role", { _user_id: userId, _role: "moderator" }),
       supabase.rpc("has_role", { _user_id: userId, _role: "superadmin" }),
     ]);

      const isAdm = !!adminRes.data || !!superRes.data;
      setIsAdmin(isAdm);
      setIsModerator(!!modRes.data);
      setIsSuperAdmin(!!superRes.data);
   };

  const refreshProfile = async () => {
     if (user) {
       await fetchProfile(user.id);
       await checkRoles(user.id, user.email);
     }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(async () => {
             await fetchProfile(session.user.id);
             await checkRoles(session.user.id, session.user.email);
             setLoading(false);
           }, 0);
         } else {
           setProfile(null);
           setIsAdmin(false);
           setIsModerator(false);
           setIsSuperAdmin(false);
           setLoading(false);
         }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
       if (session?.user) {
         fetchProfile(session.user.id);
         checkRoles(session.user.id, session.user.email);
       }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: window.location.origin,
      },
    });
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signOut = async () => {
     await supabase.auth.signOut();
     setProfile(null);
     setIsAdmin(false);
     setIsModerator(false);
     setIsSuperAdmin(false);
   };

  return (
    <AuthContext.Provider
       value={{ 
         session, user, profile, isAdmin, isModerator, isSuperAdmin, 
         loading, signUp, signIn, signOut, refreshProfile 
       }}
    >
      {children}
    </AuthContext.Provider>
  );
};
